import { captureException } from "@sentry/browser";
import { contentTemplateInstanceNewWrapper } from "building-generation/contentTemplates/contentTemplateInstanceWrapper";
import { evaluateContentTemplateInstance } from "building-generation/contentTemplates/planSetsEvaluate";
import { buildingGroundZ } from "building-generation/elements/level/buildingGroundZ";
import generateBuilding from "building-generation/generation/generateBuilding";
import type { GenerateBuildingMirrorOptions } from "building-generation/types/generateBuildingTypes";
import { collectBuildingProps } from "building-geometry/props/collectBuildingProps";
import type { Annotation } from "building-types/annotation/annotationTypes";
import { AnnotationType, TextAlignment, TextVerticalAlignment } from "building-types/annotation/annotationTypesEnum";
import type { Building, GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { CustomDataSequence } from "building-types/dataSequences/dataSequence";
import { CUSTOM_SHEET_DATA_SEQUENCE_COLUMNS } from "building-types/dataSequences/dataSequence";
import type { BuiltInDataTableSettings } from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { SHEETS_DATA_SEQUENCE_ID } from "building-types/dataSequences/systemDataSequenceTypes";
import type {
  DataTableColumnSortOrder,
  DataTableColumnConfig,
  DataTableColumnWidths,
} from "building-types/dataTables/dataTable";
import { DATA_TABLE_SYMBOL_COLUMN_ID } from "building-types/dataTables/dataTable";
import {
  decodeColumnConfigId,
  encodeColumnConfigId,
  resolveDataTableTitleConfig,
} from "building-types/dataTables/dataTableTools";
import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type { IsolatableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { doorFaceCategories, FaceCategory } from "building-types/materials/faceCategories";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { ParameterValue, ParameterValueHandle } from "building-types/options/parameter";
import { ZERO_INCHES } from "building-types/plans/constants";
import { InteriorDimensionType, PlanLineStyle } from "building-types/settings/settingsTypesEnum";
import { isViewportCameraKey, type ViewportCameraKey } from "building-types/views/CameraKey";
import { EffectType, type VisibilityEffect } from "building-types/views/effectTypes";
import type { ElevationAnnotationParams } from "building-types/views/elevationAnnotationTypes";
import type { ElevationDebugOptions } from "building-types/views/elevationAnnotationTypes";
import { GradeLineSource } from "building-types/views/GradeLineSource";
import type {
  EvaluatedDrawingViewport,
  EvaluatedWithOptionSetOverrides,
  PlanViewport,
  PlanViewTemplate,
} from "building-types/views/planSets";
import {
  DEFAULT_VIEWPORT_SCALE,
  ViewportScalingMethod,
  type ContentTemplateInstance,
  type EvaluatedDataTable,
  type EvaluatedViewport,
} from "building-types/views/planSets";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { sheetGeometry3DCategories } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry, SheetGeometryList } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { DimensionPrecision } from "building-types/views/viewTypes";
import colors from "common/constants/colorsOld";
import type { RWTable, Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined, assertIsUndefined, ensureDefined } from "common/utils/assertOfType";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { LRUStringCache } from "common/utils/lruStringCache";
import { levelInchesNew, numMul, percentNew, sheetInchesNew, sheetPointsNew } from "common/utils/nominalNumbers";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { replaceAll } from "common/utils/replaceAll";
import shallowCompare from "common/utils/shallowCompare";
import type { Mutable } from "common/utils/types";
import {
  isCompositeLevelContentSourceId,
  isMultiMemberCompositeLevelContentSourceId,
  resolveContentSourceIdToLevelIds,
  viewportContentSourceRenderId,
} from "doc/docViewport";
import { DocEntityNodeRegistry } from "doc/entity/docEntityNodeRegistry";
import { docContentTemplateSelectAnnotations } from "doc/entity/docEntityNodes/ContentTemplateDocEntityNode";
import { collectParameterIdsInString } from "doc/parameterTools";
import { inches } from "doc/pdf/drawingLayout";
import { resolveAnnotationParameterContext } from "doc/plans/annotationParameterContext";
import { type PerspectiveDrawingCamera } from "doc/plans/drawingCameraTypes";
import { elevationOcclusionEngineForFeatures } from "doc/plans/elevationAnnotationCommonParams";
import type { AbstractPlanGeometrySource, ElevationGenerationContext } from "doc/plans/planGeometrySource";
import { defaultElevationDebugOptions, makePlanGeometryList, PlanGeometrySource } from "doc/plans/planGeometrySource";
import type {
  GroupOfTaggedPlanGeometries,
  PlanGeometryGenerationDiagnostic,
  SheetGeometryStyler,
  TaggedPlanGeometry,
} from "doc/plans/planGeometrySourceTypes";
import { elevationDefaultCategoryStyles, planDefaultCategoryStyles } from "doc/plans/planStyleSheets";
import {
  makeSheetGeometrySourceFilter,
  makeSheetGeometryStyler,
  makeSortedSelectorEffectMap,
  relaxationInteractionMapFromEffects,
} from "doc/plans/sheetGeometryEffects";
import {
  builtInElevationVisibilitySelectors,
  builtInPerspectiveVisibilitySelectors,
  type SheetGeometrySelectorEffectMap,
} from "doc/plans/SheetGeometrySelectorEffectMap";
import { optionChoiceParameterValues } from "doc/systemParameterTools";
import { BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_INCLUDES_SYMBOL_COLUMN } from "doc/utils/dataSequenceTools";
import { BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_TO_ATTRIBUTE_DISPLAY_NAME } from "doc/utils/dataSequenceTools";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";
import {
  faceCategoryFilterFromSheetGeometryCategories,
  isElevationViewTemplate,
  isSectionViewTemplate,
  viewTemplateContainsElevationAnnotation,
  viewTemplateLineworkHasFaceCategory,
} from "doc/viewTemplateTools";
import { aabb2New } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { mat4LookAt } from "geom/mat4";
import type { Mat4 } from "geom/mat4";
import {
  getElevationScaleFactor,
  scaleElevationAnnotationParams,
} from "lib/components/studio/elevationAnnotationUtils";
import { emptySVG } from "lib/house/annotation/EmptySVG";
import type { BuiltInDataTable } from "lib/house/dataSequences/dataSequenceCatalog";
import { BUILTIN_DATASEQUENCE_CATALOG } from "lib/house/dataSequences/dataSequenceCatalog";
import {
  makeDataSequenceKAMFilter,
  makeDataTableKAMHeaderCellGenerator,
  makeDataTableKAMRowCellGenerator,
} from "lib/house/dataSequences/dataTableCellGenerators";
import {
  attributeFilterCheck,
  extractBuiltInDataTableSettingHeaders,
  makeBuiltInDataTables,
} from "lib/house/dataSequences/dataTableSelectors";
import {
  getBuiltInDataTableSettings,
  getDefaultBuiltInDataTableSettings,
} from "lib/house/dataSequences/getBuiltInDataTableSettings";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import { PlanSetBSPProvider } from "lib/house/pdf/planSetBSPProvider";
import {
  orthographicDrawingBB,
  renderBuildingOrthographicDrawing,
  renderBuildingPerspectiveDrawing,
} from "lib/house/pdf/renderBuildingPerspectiveDrawing";
import { renderSectionFrontPlaneIntersectionsGeometry } from "lib/house/pdf/renderSectionFrontPlaneIntersections";
import type { Linework3DGeometryAndBB } from "lib/house/plans/perspectiveTypes";
import { resolveItemReportingUnitLabelId } from "lib/house/plans/reportingUnitFilterUtils";
import { renderViewSpaceBBFromVertices } from "lib/house/render/renderNDCBBFromBuilding";
import { renderNDCBBFromBuilding } from "lib/house/render/renderNDCBBFromBuilding";
import type { DataTablePricingContext } from "lib/options/calculatePricing";
import { docComputeDelta, type DocDelta } from "lib/studio/docDelta";
import { selectFieldsThatAffectBuilding } from "lib/studio/docTools";
import type { EditorDebugPrefs } from "lib/studio/editorTypes";
import { docForGeneration } from "lib/studio/hooks/mergedOptionSetsForGeneration";
import { contentTemplateInstanceNodeToSheetGeometry, viewTemplateCamera3D } from "lib/studio/planSetTemplateToPages";
import { effectiveViewportScale, isElevationViewport, viewportContentRotationRad } from "lib/studio/viewportTools";
import type {
  GetOrthographicViewGeometryRequestPayload,
  GetOrthographicViewGeometryResponsePayload,
  GetPerspectiveViewGeometryRequestPayload,
  GetPerspectiveViewGeometryResponsePayload,
  GetTableContentsResponsePayload,
  PlanSetRequestPayload,
  PlanSetResponsePayload,
  Render3DResponsePayload,
  RenderDrawingViewportRequestPayload,
  RenderOrthographicDrawingRequestPayload,
  RenderPerspectiveDrawingRequestPayload,
  RenderPlanRequestPayload,
  RenderGeometryResponsePayload,
  RenderSectionIntersectionsRequestPayload,
  RenderSectionIntersectionsResponsePayload,
  ViewportPayloadBase,
} from "lib/workers/planSetComputeWorkerMessages";
import { STANDARD_FONT_SIZE } from "pdf/planFonts";
import { collectBuildingFacesByCategory } from "./collectBuildingFaces";
import { makeSheetNodeStyleSheet } from "./nodes/nodeStyles";
import type { TableCellStyle } from "./nodes/nodeStylesTypes";
import { type TableCellProps, TableCellContentType } from "./nodes/tableCellTypes";
import { makeTable } from "./nodes/tableSheetNode";
import {
  elevationGeometryGenerators,
  initElevationContextIntermediateData,
  initPlanContextIntermediateData,
  initSectionContextIntermediateData,
  planGeometryGenerators,
  sectionGeometryGenerators,
  slowElevationGeometryGenerators,
} from "./planGeometryGenerators";
import { selectModelSpaceAnnotation } from "./planSetAnnotationSelectors";
import { planSetCacheKey } from "./planSetCacheKey";
import {
  makeReportingUnitSheetGeometrySelector,
  makeRoomReportingUnitSheetGeometrySelectors,
} from "./reportingUnitSheetGeometrySelector";
import { makeBuiltInPlanTypeSelectorsAndFilters } from "./sheetGeometryBuiltInTypes";
import { defaultInteractionMap } from "./sheetGeometryDefaultInteractionMap";
import { createGradientDescentRelaxer } from "./sheetGeometryGradientDescent";
import type { SheetTitle } from "./sheetInfo";
import { type OrthographicDrawing, type PerspectiveDrawing } from "./sheetNode";
import { SheetNodeType } from "./sheetNode";
import { filterViewOverridesWithOptionChoices } from "./viewConditionTools";
import { applyViewOverrideToSheetGeometryCategoryTableFilter } from "./viewOverrideTools";
import { viewportEyePosAndUpVec } from "./viewportEyePos";
import {
  resolveOptionChoices,
  type ExplicitPlanViewportInputs,
  type ExplicitViewportInputs,
} from "./viewportRenderInputs";

function docContentTemplateInstanceContainsViewport(
  doc: Doc,
  instance: ContentTemplateInstance,
  visitedInstanceIds: Set<string> = new Set(),
) {
  if (!instance) {
    return false;
  }

  // Content template instances can form cycles through nested templates.
  // Track the traversal path by instance id so sibling branches can still
  // evaluate the same template through different instances.
  if (visitedInstanceIds.has(instance.id)) {
    return false;
  }

  const template = doc.contentTemplates[instance.contentTemplateId];
  if (!template) {
    return false;
  }

  const nextVisitedInstanceIds = new Set(visitedInstanceIds);
  nextVisitedInstanceIds.add(instance.id);

  const templateNode = DocEntityNodeRegistry.getNode(doc, { type: "CONTENT_TEMPLATE", id: template.id });

  if (!templateNode) {
    return false;
  }

  const children = templateNode.getChildrenOfType("VIEWPORT");

  if (children.length > 0) {
    return true;
  }

  const childInstances = templateNode.getChildrenOfType("CONTENT_TEMPLATE_INSTANCE");

  for (const childInstance of childInstances) {
    if (docContentTemplateInstanceContainsViewport(doc, childInstance.getDocEntity(), nextVisitedInstanceIds)) {
      return true;
    }
  }

  return false;
}

/**
 * Returns true if any annotations in the table reference the `currentTime` system parameter
 * via `{{ currentTime }}` token syntax. Used to determine whether a viewport's cache entry
 * should be skipped (since currentTime changes frequently and would invalidate the cache).
 */
export function annotationsReferenceCurrentTime(annotations: Annotation[]): boolean {
  for (const annot of annotations) {
    if (annot.type === AnnotationType.NOTE && collectParameterIdsInString(annot.text)["currentTime"]) {
      return true;
    }
  }
  return false;
}

/**
 * Selects parts of Doc that will affect the building and any model space hosted
 * annotation. Note that the annotation table is flattened into the Doc object.
 * The shape of this data structure is rather unusual and is only intended for use
 * as a shallow compared cache key.
 */
export function selectFieldsThatAffectBuildingAndModelSpaceAnnotation(doc: Doc): Partial<Doc> {
  const fields = selectFieldsThatAffectBuilding(doc);

  /**
   * Content template annotation doesn't affect the building but does live in the
   * annotation table.
   *
   * This is an important performance optimization as otherwise editing annotation in a
   * in a sheet template will cause all of the viewports to refresh. In the future,
   * it would be nice to generalize this framework.
   **/
  const combined = {
    ...fields,
    annotation: selectModelSpaceAnnotation(doc),
    globalParameters: doc.globalParameters,
  };

  return combined;
}

/**
 * Not used by the cache, which folds the same dependency set into `hashDocBuildingAndModelSpaceAnnotation`.
 * Kept for the editor viewer memoization hooks, which compare two docs directly.
 */
export function compareFieldsThatAffectBuildingAndModelSpaceAnnotation(a: Doc, b: Doc) {
  if (a === b) {
    return true;
  }

  const fieldsA = selectFieldsThatAffectBuilding(a) as Mutable<Partial<Doc>>;
  const fieldsB = selectFieldsThatAffectBuilding(b) as Mutable<Partial<Doc>>;

  const annoA = selectModelSpaceAnnotation(a);
  const annoB = selectModelSpaceAnnotation(b);

  delete fieldsA.annotation;
  delete fieldsB.annotation;

  return (
    shallowCompare(fieldsA, fieldsB) &&
    shallowCompare(a.globalParameters, b.globalParameters) &&
    shallowCompare(a.sectionViews, b.sectionViews) &&
    shallowCompare(a.viewports, b.viewports) &&
    shallowCompare(annoA, annoB)
  );
}

const contentTemplateInstancesWithViewportsCache = new WeakMap<Doc, Table<ContentTemplateInstance>>();

export function selectContentTemplateInstancesWithViewports(doc: Doc): Table<ContentTemplateInstance> {
  const cached = contentTemplateInstancesWithViewportsCache.get(doc);
  if (cached) {
    return cached;
  }
  const result = objectKeyFilter(doc.contentTemplateInstances, (_, instance) =>
    docContentTemplateInstanceContainsViewport(doc, instance),
  );
  contentTemplateInstancesWithViewportsCache.set(doc, result);
  return result;
}

/**
 * Wraps background thread creation of plan geometry for a viewport. It makes it practical and efficient to generate
 * plan geometry by caching the output and ensuring only fragments of the document are transferred to the worker.
 */
export abstract class PlanSetComputeServiceBase {
  private lastDocSync: Doc | undefined;

  /** Keyed by {@link planSetCacheKey}, so an entry retains a few KB rather than a whole `Doc`. */
  static readonly cache = new Map<PlanSetRequestPayload["type"], LRUStringCache<Promise<PlanSetResponsePayload>>>();

  static MAX_CACHE_SIZE_PER_TYPE = 100;

  // THIS FUNCTION MUST BE KEPT IN SYNC with the number of promises returned by executePlanSetJob.
  // If all the functions called from executePlanSetJob were fast (i.e. never `await` inside of the functions),
  // then executePlanSetJob could be called on the worker host side, and use the number of
  // returned promises to wait for as many number of responses from the worker, without
  // actually resolving the promises on the worker host side.
  // Alternatively the worker could send a first message back with the number of promises. However,
  // that makes the worker host side function async too.
  // Another alternative is to not care about the number of responses (and associated promises) on
  // the worker host side, just use a callback function that gets called an unknown number of times --
  // perhaps with a completion flag set on the last invocation.
  protected static planSetJobNumberOfPromises(payload: PlanSetRequestPayload): number {
    if (payload.type === "RENDER_PLAN") {
      // TODO: this would be the more correct way, but that would require the
      //       doc be maintained on a per-job basis in the host side, just to be able
      //       to pass it to this method. Instead, even when we have a cached elevation
      //       view result, we'll return two promises.
      // const cached = PlanSetComputeServiceBase.checkCache(planSetCacheKey(payload, doc), payload.type);
      // return cached ? 1 : renderPlanNumberOfPromises(payload.viewType);
      return renderPlanNumberOfPromises(payload.viewType);
    }
    return 1;
  }

  protected static checkCache(key: string, type: PlanSetRequestPayload["type"]) {
    return PlanSetComputeServiceBase.cache.get(type)?.get(key);
  }

  protected static addToCache(
    key: string,
    type: PlanSetRequestPayload["type"],
    promise: Promise<PlanSetResponsePayload>,
  ) {
    let cacheForType = PlanSetComputeServiceBase.cache.get(type);
    if (!cacheForType) {
      // Per-type budgets, so a burst of one job kind can't evict every result of another.
      cacheForType = new LRUStringCache<Promise<PlanSetResponsePayload>>(
        PlanSetComputeServiceBase.MAX_CACHE_SIZE_PER_TYPE,
      );
      PlanSetComputeServiceBase.cache.set(type, cacheForType);
    }
    cacheForType.add(key, promise);
  }

  public static clearCache() {
    PlanSetComputeServiceBase.cache.clear();
  }

  protected resetDocSync(): void {
    this.lastDocSync = undefined;
  }

  public static cacheEntryCount(type?: PlanSetRequestPayload["type"]): number {
    if (type !== undefined) {
      return PlanSetComputeServiceBase.cache.get(type)?.count ?? 0;
    }
    let total = 0;
    for (const cacheForType of PlanSetComputeServiceBase.cache.values()) {
      total += cacheForType.count;
    }
    return total;
  }

  private static viewportDependsOnCurrentTime(msg: PlanSetRequestPayload, doc: Doc): boolean {
    if (msg.type === "RENDER_PLAN") {
      const annotations = Object.values(selectModelSpaceAnnotation(doc));
      return annotationsReferenceCurrentTime(annotations);
    }
    if (msg.type === "RENDER_DRAWING_VIEWPORT") {
      const templateAnnotations = docContentTemplateSelectAnnotations(
        doc,
        msg.viewport.contentSourceId,
        DocEntityNodeRegistry.getSource(),
      );
      return annotationsReferenceCurrentTime(templateAnnotations);
    }
    return false;
  }

  protected abstract executeJob(
    msg: PlanSetRequestPayload,
    docDelta: DocDelta,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<PlanSetResponsePayload>[];

  private static cacheStats = {
    hits: 0,
    misses: 0,
  };

  public static get cacheHitPercentage() {
    const totalQueries = PlanSetComputeServiceBase.cacheStats.hits + PlanSetComputeServiceBase.cacheStats.misses;
    if (totalQueries === 0) {
      return 0;
    }
    return PlanSetComputeServiceBase.cacheStats.hits / totalQueries;
  }

  public static resetCacheStats() {
    PlanSetComputeServiceBase.cacheStats.hits = 0;
    PlanSetComputeServiceBase.cacheStats.misses = 0;
  }

  private executeJobWithCache(
    msg: PlanSetRequestPayload,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<PlanSetResponsePayload>[] {
    const cacheKey = planSetCacheKey(msg, doc, isolatedEntity);
    const cached = PlanSetComputeServiceBase.checkCache(cacheKey, msg.type);
    if (cached) {
      PlanSetComputeServiceBase.cacheStats.hits++;
      if (msg.type === "RENDER_PLAN") {
        // See the TODO comment on planSetJobNumberOfPromises
        return Array.from({ length: renderPlanNumberOfPromises(msg.viewType) }, () => cached);
      }

      return [cached];
    }

    PlanSetComputeServiceBase.cacheStats.misses++;
    const docDelta = docComputeDelta(this.lastDocSync, doc);

    this.lastDocSync = doc;

    const promises = this.executeJob(msg, docDelta, enabledFeatures, isolatedEntity);

    // Don't cache viewports whose output depends on currentTime, since it changes frequently.
    if (!PlanSetComputeServiceBase.viewportDependsOnCurrentTime(msg, doc)) {
      // The job chunks return progressively more detailed geometry,
      // therefore the last one is the most detailed, and therefore the
      // one we want to cache.
      PlanSetComputeServiceBase.addToCache(cacheKey, msg.type, promises[promises.length - 1]);
    }

    return promises;
  }

  private static buildViewportPayload({
    viewport,
    viewTemplate,
    optionChoices,
    renderStyleMode,
  }: ExplicitViewportInputs): ViewportPayloadBase {
    const { viewType, contentSourceId, dimensionPrecision } = viewport;

    const contentSourceRenderId =
      viewport.viewType === "PLAN_VIEW"
        ? viewportContentSourceRenderId(viewport.contentSourceId, viewport.contentSourceComponentInstanceChain)
        : contentSourceId;

    return {
      viewType,
      optionChoices,
      viewTemplate,
      contentSourceId: contentSourceRenderId,
      selectedReportingUnitLabelId: viewport.selectedReportingUnitLabelId,
      dimensionPrecision,
      renderStyleMode,
    };
  }

  private static buildExplicitViewportInputs(
    viewport: EvaluatedViewport,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): ExplicitViewportInputs {
    return {
      doc,
      viewport,
      viewTemplate: doc.viewTemplates[viewport.viewTemplateId],
      optionChoices: resolveOptionChoices(doc, viewport),
      enabledFeatures,
      isolatedEntity,
    };
  }

  async renderPerspectivePreviewFromInputs({
    doc,
    viewport,
    viewTemplate,
    optionChoices,
    enabledFeatures,
    isolatedEntity,
  }: ExplicitViewportInputs): Promise<Render3DResponsePayload> {
    return this.executeJobWithCache(
      {
        type: "GET_PERSPECTIVE_VIEW_GEOMETRY",
        ...PlanSetComputeServiceBase.buildViewportPayload({
          doc,
          viewport,
          viewTemplate,
          optionChoices,
          enabledFeatures,
        }),
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<Render3DResponsePayload>;
  }

  async renderPerspectivePreview(
    viewport: EvaluatedViewport,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<Render3DResponsePayload> {
    return this.renderPerspectivePreviewFromInputs(
      PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
    );
  }

  async renderOrthographicPreviewFromInputs({
    doc,
    viewport,
    viewTemplate,
    optionChoices,
    enabledFeatures,
    isolatedEntity,
  }: ExplicitViewportInputs): Promise<GetOrthographicViewGeometryResponsePayload> {
    return this.executeJobWithCache(
      {
        type: "GET_ORTHOGRAPHIC_VIEW_GEOMETRY",
        ...PlanSetComputeServiceBase.buildViewportPayload({
          doc,
          viewport,
          viewTemplate,
          optionChoices,
          enabledFeatures,
        }),
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<GetOrthographicViewGeometryResponsePayload>;
  }

  async renderOrthographicPreview(
    viewport: EvaluatedViewport,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<GetOrthographicViewGeometryResponsePayload> {
    return this.renderOrthographicPreviewFromInputs(
      PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
    );
  }

  async renderSectionIntersectionsFromInputs({
    doc,
    viewport,
    viewTemplate,
    optionChoices,
    enabledFeatures,
    isolatedEntity,
  }: ExplicitViewportInputs): Promise<RenderSectionIntersectionsResponsePayload> {
    return this.executeJobWithCache(
      {
        type: "RENDER_SECTION_INTERSECTIONS",
        ...PlanSetComputeServiceBase.buildViewportPayload({
          doc,
          viewport,
          viewTemplate,
          optionChoices,
          enabledFeatures,
        }),
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<RenderSectionIntersectionsResponsePayload>;
  }

  async renderSectionIntersections(
    viewport: EvaluatedViewport,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<RenderSectionIntersectionsResponsePayload> {
    return this.renderSectionIntersectionsFromInputs(
      PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
    );
  }

  renderPlanFromInputs({
    doc,
    viewport,
    viewTemplate,
    optionChoices,
    renderStyleMode,
    globalParameterValues,
    debugPrefs,
    enabledFeatures,
    planSetTemplateId,
    planSetTemplateConfigId,
    isolatedEntity,
  }: ExplicitPlanViewportInputs): Promise<RenderGeometryResponsePayload>[] {
    return this.executeJobWithCache(
      {
        type: "RENDER_PLAN",
        ...PlanSetComputeServiceBase.buildViewportPayload({
          doc,
          viewport,
          viewTemplate,
          optionChoices,
          renderStyleMode,
          enabledFeatures,
        }),
        viewport,
        globalParameterValues,
        debugPrefs,
        planSetTemplateId,
        planSetTemplateConfigId,
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    ) as Promise<RenderGeometryResponsePayload>[];
  }

  renderPlan(
    viewport: EvaluatedViewport,
    doc: Doc,
    globalParameterValues: Table<ParameterValue>,
    debugPrefs?: EditorDebugPrefs,
    enabledFeatures?: GenerateBuildingFeatureSet,
    planSetTemplateId?: DocEntityId<"PLAN_SET_TEMPLATE">,
    planSetTemplateConfigId?: DocEntityId<"PLAN_SET_TEMPLATE_CONFIG">,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<RenderGeometryResponsePayload>[] {
    return this.renderPlanFromInputs({
      ...PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
      globalParameterValues,
      debugPrefs,
      planSetTemplateId,
      planSetTemplateConfigId,
    });
  }

  async renderDrawingViewportFromInputs({
    doc,
    viewport,
    viewTemplate,
    optionChoices,
    globalParameterValues,
    enabledFeatures,
    isolatedEntity,
  }: ExplicitPlanViewportInputs<EvaluatedDrawingViewport>): Promise<RenderGeometryResponsePayload> {
    return this.executeJobWithCache(
      {
        type: "RENDER_DRAWING_VIEWPORT",
        ...PlanSetComputeServiceBase.buildViewportPayload({
          doc,
          viewport,
          viewTemplate,
          optionChoices,
          enabledFeatures,
        }),
        viewport,
        globalParameterValues,
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<RenderGeometryResponsePayload>;
  }

  async renderDrawingViewport(
    viewport: EvaluatedDrawingViewport,
    doc: Doc,
    globalParameterValues: Table<ParameterValue> = {},
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<RenderGeometryResponsePayload> {
    return this.renderDrawingViewportFromInputs({
      ...PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
      viewport,
      globalParameterValues,
    });
  }

  async renderPerspectiveDrawing(
    drawing: PerspectiveDrawing,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<Linework3DGeometryAndBB> {
    const viewport = drawing.viewport;
    if (!(viewport)) {
      console.assert(false);
    }

    if (!viewport) {
      return Promise.resolve({
        planGeometryList: { geometry: [] },
        bb: aabb2New(0, 0, 0, 0),
      });
    }

    return this.executeJobWithCache(
      {
        type: "RENDER_PERSPECTIVE_DRAWING",
        ...PlanSetComputeServiceBase.buildViewportPayload(
          PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
        ),
        perspectiveDrawing: drawing,
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<Linework3DGeometryAndBB>;
  }

  async renderOrthographicDrawing(
    drawing: OrthographicDrawing,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<Linework3DGeometryAndBB> {
    const viewport = drawing.viewport;
    if (!(viewport)) {
      console.assert(false);
    }

    if (!viewport) {
      return Promise.resolve({
        planGeometryList: { geometry: [] },
        bb: aabb2New(0, 0, 0, 0),
      });
    }

    return this.executeJobWithCache(
      {
        type: "RENDER_ORTHOGRAPHIC_DRAWING",
        ...PlanSetComputeServiceBase.buildViewportPayload(
          PlanSetComputeServiceBase.buildExplicitViewportInputs(viewport, doc, enabledFeatures, isolatedEntity),
        ),
        drawing,
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<Linework3DGeometryAndBB>;
  }

  async getTableContents(
    dataTable: EvaluatedDataTable,
    doc: Doc,
    enabledFeatures?: GenerateBuildingFeatureSet,
    pricingContext?: DataTablePricingContext,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<GetTableContentsResponsePayload> {
    return this.executeJobWithCache(
      {
        type: "GET_TABLE_CONTENTS",
        dataTable,
        pricingContext,
      },
      doc,
      enabledFeatures,
      isolatedEntity,
    )[0] as Promise<GetTableContentsResponsePayload>;
  }
}

type BSPCache = LRUObjectCache<BSPCacheKey, BSPCachedContext>;
type BuildingCache = LRUObjectCache<BuildingCacheKey, BuildingCachedContext>;
type Building3DGeometryCache = LRUObjectCache<BuildingCacheKey, Building3DGeometryCachedContext>;

export interface BSPCachedContext {
  bspProvider: PlanSetBSPProvider;
}

export interface BuildingCachedContext {
  building: Building;
}

export interface Building3DGeometryCachedContext {
  groups: ReturnType<typeof collectBuildingFacesByCategory>;
  props: ReturnType<typeof collectBuildingProps>;
  groundZ: number;
}

export interface BSPCacheKey {
  readonly doc: Doc;
  readonly optionChoices: OptionChoices;
  readonly isolatedEntity?: IsolatableEntityHandle;
  readonly mirrorInvert?: boolean;
  readonly mirrorTranslationXOverride?: number;
}

export interface BuildingCacheKey {
  readonly doc: Doc;
  readonly optionChoices: OptionChoices;
  readonly globalParamValues: Table<ParameterValue>;
  readonly isolatedEntity?: IsolatableEntityHandle;
  readonly mirrorInvert?: boolean;
  readonly mirrorTranslationXOverride?: number;
}

export function compareIsolatedEntity(a?: IsolatableEntityHandle, b?: IsolatableEntityHandle): boolean {
  return a?.type === b?.type && a?.id === b?.id;
}

export function compareBspCacheKeys(a: BSPCacheKey, b?: BSPCacheKey): boolean {
  return (
    a.doc === b?.doc &&
    shallowCompare(a.optionChoices, b?.optionChoices) &&
    compareIsolatedEntity(a.isolatedEntity, b?.isolatedEntity) &&
    a.mirrorInvert === b?.mirrorInvert &&
    a.mirrorTranslationXOverride === b?.mirrorTranslationXOverride
  );
}

export function compareBuildingCacheKeys(a: BuildingCacheKey, b?: BuildingCacheKey): boolean {
  return (
    a.doc === b?.doc &&
    shallowCompare(a.optionChoices, b?.optionChoices) &&
    shallowCompare(a.globalParamValues, b?.globalParamValues) &&
    compareIsolatedEntity(a.isolatedEntity, b?.isolatedEntity) &&
    a.mirrorInvert === b?.mirrorInvert &&
    a.mirrorTranslationXOverride === b?.mirrorTranslationXOverride
  );
}

function getBspCachedContext(cacheKey: BSPCacheKey, cache: BSPCache): BSPCachedContext | null {
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }
  return null;
}

function getBuildingCachedContext(cacheKey: BuildingCacheKey, cache: BuildingCache): BuildingCachedContext | null {
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }
  return null;
}

function getBuilding3DGeometryCachedContext(
  cacheKey: BuildingCacheKey,
  cache: Building3DGeometryCache,
): Building3DGeometryCachedContext | null {
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }
  return null;
}

const COMPOSITE_LEVEL_CATEGORIES = new Set([
  SheetGeometryCategory.WALL_BOUNDARIES,
  SheetGeometryCategory.WALL_CORE,
  SheetGeometryCategory.WALL_SUBSTRATE,
  SheetGeometryCategory.WALL_EXTERIOR_FINISH,
  SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED,
  SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_LINE,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS,
  SheetGeometryCategory.WALL_EXTERIOR_DIM_GROUP,
]);

const COMPOSITE_INTERIOR_DIM_CATEGORIES = new Set([
  SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL,
  SheetGeometryCategory.WALL_INTERIOR_DIM_LINE,
  SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK,
  SheetGeometryCategory.WALL_INTERIOR_DIM_TICK,
  SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS,
  SheetGeometryCategory.WALL_INTERIOR_DIM_GROUP,
]);

const REPORTING_UNIT_VISIBILITY_EFFECT: VisibilityEffect = {
  id: "reportingUnitVisibility",
  entityType: "EFFECT",
  name: "Reporting Unit Visibility",
  sortIndex: 0,
  type: EffectType.VISIBILITY,
  categories: [],
  invert: true,
};

export class LocalPlanSetComputeService extends PlanSetComputeServiceBase {
  private bspCache = new LRUObjectCache<BSPCacheKey, BSPCachedContext>(10, compareBspCacheKeys);
  private buildingCache = new LRUObjectCache<BuildingCacheKey, BuildingCachedContext>(10, compareBuildingCacheKeys);
  private building3DGeometryCache = new LRUObjectCache<BuildingCacheKey, Building3DGeometryCachedContext>(
    10,
    compareBuildingCacheKeys,
  );

  constructor(
    private doc: Doc,
    private enabledFeatures?: GenerateBuildingFeatureSet,
    private isolatedEntity?: IsolatableEntityHandle,
    private mirrorOptions?: GenerateBuildingMirrorOptions,
  ) {
    super();
  }

  protected executeJob(
    msg: PlanSetRequestPayload,
    _docDelta: DocDelta,
    enabledFeatures?: GenerateBuildingFeatureSet,
    isolatedEntity?: IsolatableEntityHandle,
  ): Promise<PlanSetResponsePayload>[] {
    return executePlanSetJob(
      this.doc,
      msg,
      this.bspCache,
      this.buildingCache,
      this.building3DGeometryCache,
      enabledFeatures ?? this.enabledFeatures,
      isolatedEntity ?? this.isolatedEntity,
      this.mirrorOptions,
    );
  }
}

export function executePlanSetJob(
  doc: Doc,
  payload: PlanSetRequestPayload,
  bspCache?: BSPCache,
  buildingCache?: BuildingCache,
  building3DGeometryCache?: Building3DGeometryCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<PlanSetResponsePayload>[] {
  switch (payload.type) {
    case "GET_PERSPECTIVE_VIEW_GEOMETRY":
      if (payload.viewTemplate.viewType !== "VIEW_3D") {
        throw new Error("Invalid view type for rendering a perspective view: " + payload.viewTemplate.viewType);
      }
      if (!isViewportCameraKey(payload.contentSourceId)) {
        throw new Error("Invalid content source for rendering a perspective view");
      }
      if (payload.optionChoices === null) {
        throw new Error("Option choices are required for rendering a perspective view");
      }
      return [
        collectPerspectiveViewGeometry(
          doc,
          { ...payload, optionChoices: payload.optionChoices },
          buildingCache,
          building3DGeometryCache,
          enabledFeatures,
          isolatedEntity,
          mirrorOptions,
        ),
      ];
    case "GET_ORTHOGRAPHIC_VIEW_GEOMETRY":
      if (payload.viewTemplate.viewType === "VIEW_3D") {
        throw new Error("Invalid view type for rendering an orthographic view: " + payload.viewTemplate.viewType);
      }
      if (payload.optionChoices === null) {
        throw new Error("Option choices are required for rendering an orthographic view");
      }
      return [
        collectOrthographicViewGeometry(
          doc,
          { ...payload, optionChoices: payload.optionChoices },
          buildingCache,
          building3DGeometryCache,
          enabledFeatures,
          isolatedEntity,
          mirrorOptions,
        ),
      ];
    case "RENDER_DRAWING_VIEWPORT":
      return [generateDrawingViewport(doc, payload)];
    case "RENDER_SECTION_INTERSECTIONS":
      return [
        generateSectionIntersections(doc, payload, buildingCache, enabledFeatures, isolatedEntity, mirrorOptions),
      ];
    case "RENDER_PLAN":
      return generatePlanGeometryForView(doc, payload, buildingCache, enabledFeatures, isolatedEntity, mirrorOptions);
    case "GET_TABLE_CONTENTS":
      return [
        generateDataTableContents(
          doc,
          payload.dataTable,
          undefined,
          buildingCache,
          enabledFeatures,
          payload.pricingContext,
          isolatedEntity,
          mirrorOptions,
        ),
      ];
    case "RENDER_PERSPECTIVE_DRAWING":
      return [
        generateBuildingAndPerspectiveDrawing(
          doc,
          payload,
          bspCache,
          buildingCache,
          enabledFeatures,
          isolatedEntity,
          mirrorOptions,
        ),
      ];
    case "RENDER_ORTHOGRAPHIC_DRAWING":
      return [
        generateBuildingAndOrthographicDrawing(
          doc,
          payload,
          bspCache,
          buildingCache,
          enabledFeatures,
          isolatedEntity,
          mirrorOptions,
        ),
      ];
  }
}

async function generateDrawingViewport(
  doc: Doc,
  payload: RenderDrawingViewportRequestPayload,
): Promise<RenderGeometryResponsePayload> {
  const { viewTemplate, viewport, globalParameterValues } = payload;

  const { contentSourceId } = viewport;

  const dwg = doc.contentTemplates[contentSourceId];
  if (!dwg) {
    console.assert(false, "Invalid drawing content source id");
    return { geometry: { geometry: [] } };
  }

  // ContentTemplateInstance expects parameter values in the form of handles, so we need to convert them.
  const parameterValueHandles: RWTable<ParameterValueHandle> = {};
  for (const key in viewport.parameterValues) {
    parameterValueHandles[key] = { type: "PRIMITIVE", value: viewport.parameterValues[key] };
  }

  const instanceNode = evaluateContentTemplateInstance({
    instance: contentTemplateInstanceNewWrapper(
      docEntityIdFromString("CONTENT_TEMPLATE", contentSourceId),
      "DRAWING",
      parameterValueHandles,
    ),
    doc,
    globalParamValues: globalParameterValues,
  });

  const sheetGeom = contentTemplateInstanceNodeToSheetGeometry(instanceNode, doc);

  const { categories } = viewTemplate;

  const selectorEffectMap = makeSortedSelectorEffectMap(
    viewport.viewTemplateOverrideIds,
    viewTemplate.viewTemplateOverrideIds,
    doc,
  );

  const styler = makeSheetGeometryStyler({
    selectorEffectMap,
    viewSpecificStyles: viewTemplate.styleOverrides,
    effects: doc.effects,
  });

  const filtering = makeSheetGeometrySourceFilter({
    selectorEffectMap,
  });

  // Make minimal plan geometry source for compatibility
  const planGeometrySource: AbstractPlanGeometrySource = {
    getPlanGeometry: (category: SheetGeometryCategory): Promise<TaggedPlanGeometry[]> => {
      // these already have categories given the filter, so we can just cast this
      return Promise.resolve(sheetGeom.filter((x) => x.category === category) as TaggedPlanGeometry[]);
    },

    getGroupOfGeometries: (_tag: SheetGeometryCategory): Promise<GroupOfTaggedPlanGeometries[]> => {
      return Promise.resolve([]);
    },
  };

  return {
    geometry: await makePlanGeometryList({
      categoriesToRender: categories,
      filtering,
      planGeometrySource,
      styling: styler,
    }),
  };
}

async function generateBuildingWithOptionChoices(
  doc: Doc,
  optionChoices: OptionChoices,
  globalParamValues: Table<ParameterValue>,
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<Building> {
  const buildingCacheKey: BuildingCacheKey = {
    doc,
    optionChoices,
    globalParamValues,
    isolatedEntity,
    mirrorInvert: mirrorOptions?.mirrorInvert,
    mirrorTranslationXOverride: mirrorOptions?.mirrorTranslationXOverride,
  };

  if (buildingCache) {
    const cached = getBuildingCachedContext(buildingCacheKey, buildingCache);
    if (cached) {
      return cached.building;
    }
  }

  const building = await generateBuilding({
    name: "",
    doc: docForGeneration(doc),
    optionChoices,
    globalParamValues,
    showPerformance: false,
    printWarningsToConsole: false,
    useArenaAlloc: false,
    throwOnError: true,
    enabledFeatures,
    isolatedEntity,
    mirrorInvert: mirrorOptions?.mirrorInvert,
    mirrorTranslationXOverride: mirrorOptions?.mirrorTranslationXOverride,
  });

  if (buildingCache) {
    buildingCache.add(buildingCacheKey, { building });
  }

  return building;
}

async function generateBuildingAndPerspectiveDrawing(
  doc: Doc,
  { optionChoices, perspectiveDrawing }: RenderPerspectiveDrawingRequestPayload,
  bspCache?: BSPCache,
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<Linework3DGeometryAndBB> {
  if (optionChoices === null) {
    return { planGeometryList: { geometry: [] } };
  }

  const visibilitySelectors = builtInPerspectiveVisibilitySelectors(); // Hardcoded tag visibility filter for perspective views.
  perspectiveDrawing = {
    ...perspectiveDrawing,
    sceneOptions: { ...perspectiveDrawing.sceneOptions, visibilitySelectors },
  };

  const cacheKey: BSPCacheKey = {
    doc,
    optionChoices,
    isolatedEntity,
    mirrorInvert: mirrorOptions?.mirrorInvert,
    mirrorTranslationXOverride: mirrorOptions?.mirrorTranslationXOverride,
  };

  const globalParamValues = {}; // Never needed for 3D geometry output. TODO: Does caching perform better if we include these?
  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );

  const { viewport } = perspectiveDrawing;
  if (viewport && !perspectiveDrawing.camera) {
    if (!(viewport.viewType === "VIEW_3D")) {
      console.assert(false);
    }
    let camera: PerspectiveDrawingCamera = viewTemplateCamera3D(
      building,
      viewport.contentSourceId as ViewportCameraKey,
    );
    const aspect = perspectiveDrawing.width / perspectiveDrawing.height;
    camera = { ...camera, aspect };
    perspectiveDrawing = { ...perspectiveDrawing, camera };
  }

  if (bspCache) {
    const cached = getBspCachedContext(cacheKey, bspCache);
    if (cached) {
      return renderBuildingPerspectiveDrawing(perspectiveDrawing, building, cached.bspProvider);
    }
  }

  const bspProvider = new PlanSetBSPProvider(building);

  const ret = await renderBuildingPerspectiveDrawing(perspectiveDrawing, building, bspProvider);

  if (bspCache) {
    bspCache.add(cacheKey, { bspProvider });
  } else {
    bspProvider.dispose();
  }

  return ret;
}

async function generateBuildingAndOrthographicDrawing(
  doc: Doc,
  payload: RenderOrthographicDrawingRequestPayload,
  bspCache?: BSPCache,
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<Linework3DGeometryAndBB> {
  if (payload.optionChoices === null) {
    return { planGeometryList: { geometry: [] } };
  }

  const globalParamValues = {}; // Never needed for geometry output. TODO: Does caching perform better if we include these?
  const generatedBuilding = await generateBuildingWithOptionChoices(
    doc,
    payload.optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const building = generatedBuilding;

  let drawing: OrthographicDrawing = payload.drawing;
  if (drawing.viewport) {
    const { viewport } = drawing;

    drawing = {
      ...drawing,
      ...viewportEyePosAndUpVec(
        viewport.viewType,
        viewport.contentSourceId,
        doc,
        building.mirror,
        building.mirrorTranslationX,
      ),
    };
  }

  // Visibility selectors should not be passed from outside, they're computed from the viewport.
  assertIsUndefined(drawing.sceneOptions.visibilitySelectors);

  const selectors = makeSortedSelectorEffectMap(
    drawing.viewport?.viewTemplateOverrideIds ?? [],
    drawing.viewTemplateOverrideIds ?? [],
    doc,
  );
  const reportingUnitSelector = makeReportingUnitSheetGeometrySelector({
    doc,
    selectedReportingUnitLabelId: payload.selectedReportingUnitLabelId,
  });
  if (reportingUnitSelector) {
    const reportingUnitVisibilitySelector: SheetGeometrySelectorEffectMap = {
      selector: reportingUnitSelector,
      effects: [REPORTING_UNIT_VISIBILITY_EFFECT],
    };
    selectors.push(reportingUnitVisibilitySelector);
  }
  if (drawing.viewport?.viewType === "ELEVATION_VIEW") {
    // Hardcoded tag visibility filter for elevation views. This is applied on top of the VTOs.
    selectors.push(...builtInElevationVisibilitySelectors());
  }

  drawing = {
    ...drawing,
    sceneOptions: {
      ...drawing.sceneOptions,
      visibilitySelectors: selectors,
      selectedReportingUnitLabelId: reportingUnitSelector ? payload.selectedReportingUnitLabelId : undefined,
    },
  };

  const cacheKey: BSPCacheKey = {
    doc,
    optionChoices: payload.optionChoices,
    isolatedEntity,
    mirrorInvert: mirrorOptions?.mirrorInvert,
    mirrorTranslationXOverride: mirrorOptions?.mirrorTranslationXOverride,
  };
  if (bspCache) {
    const cached = getBspCachedContext(cacheKey, bspCache);
    if (cached) {
      return renderBuildingOrthographicDrawing(drawing, building, cached.bspProvider);
    }
  }
  const bspProvider = new PlanSetBSPProvider(building);

  const ret = renderBuildingOrthographicDrawing(drawing, building, bspProvider);

  if (bspCache) {
    bspCache.add(cacheKey, { bspProvider });
  } else {
    bspProvider.dispose();
  }

  return ret;
}

export async function generatePlanGeometryForBuilding({
  doc,
  building,
  contentSourceId,
  optionChoices,
  viewTemplate,
  viewport,
  dimensionPrecision,
  renderStyleMode,
  globalParameterValues,
  debugPrefs,
  planSetTemplateId,
  planSetTemplateConfigId,
  generationDiagnostics,
}: {
  doc: Doc;
  building: Building;
  contentSourceId: RenderPlanRequestPayload["contentSourceId"];
  optionChoices: OptionChoices;
  viewTemplate: PlanViewTemplate;
  viewport: EvaluatedWithOptionSetOverrides<PlanViewport>;
  dimensionPrecision?: DimensionPrecision;
  renderStyleMode?: RenderPlanRequestPayload["renderStyleMode"];
  globalParameterValues: Table<ParameterValue>;
  debugPrefs?: EditorDebugPrefs;
  planSetTemplateId?: DocEntityId<"PLAN_SET_TEMPLATE">;
  planSetTemplateConfigId?: DocEntityId<"PLAN_SET_TEMPLATE_CONFIG">;
  generationDiagnostics?: PlanGeometryGenerationDiagnostic[];
}): Promise<SheetGeometryList> {
  const isComposite = isMultiMemberCompositeLevelContentSourceId(doc, contentSourceId);

  // Resolve contentSourceId to level IDs (handles both single levels and composite levels).
  // Component instance levels are generated building levels and do not exist in the source doc.
  const levelIds = resolveContentSourceIdToLevelIds(doc, contentSourceId, building.levels);

  // Filter to only levels that exist in the building
  const validLevelIds = levelIds.filter((levelId) => building.levels[levelId]);

  if (validLevelIds.length === 0 || viewTemplate.viewType !== "PLAN_VIEW") {
    return { geometry: [] };
  }

  const planRotationRads = viewportContentRotationRad(viewport);

  /**
   * @NOTE - see the definition of PlanGeometryContext.svgScale for more information
   * about this property. We don't want to set this to anything except 1
   * unless we are in a BuiltInPlanSet
   *
   * This value should NOT user planSettings.annotationScale. We do not
   * want any planSettings values to affect planSetComputeService geometry
   */
  const svgTextScale = 1;

  const { referenceType, categories } = viewTemplate;
  const categoriesToRender = [...categories];
  if (debugPrefs?.debugFloorPlanRelaxation) {
    categoriesToRender.push(SheetGeometryCategory.RELAXATION_DEBUG);
  }

  const provenanceSelectors = applyViewOverrideToSheetGeometryCategoryTableFilter(
    [viewTemplate, viewport],
    filterViewOverridesWithOptionChoices(doc, optionChoices),
    doc.viewSelectors,
    doc.selectors,
  );

  const reportingUnitSelectors = makeRoomReportingUnitSheetGeometrySelectors({
    doc,
    selectedReportingUnitLabelId: viewport.selectedReportingUnitLabelId,
    categories: categoriesToRender,
  });

  const selectorEffectMap = makeSortedSelectorEffectMap(
    viewport.viewTemplateOverrideIds,
    viewTemplate.viewTemplateOverrideIds,
    doc,
  );

  const styling = makeSheetGeometryStyler({
    selectorEffectMap,
    viewSpecificStyles: viewTemplate.styleOverrides,
    effects: doc.effects,
    defaultStyles: planDefaultCategoryStyles,
  });

  const relaxationInteractionMap = relaxationInteractionMapFromEffects(selectorEffectMap);

  let interactionMap = relaxationInteractionMap ?? {};
  const weightMap = {};

  if (Object.keys(interactionMap).length === 0 && viewTemplate.reduceOverlap) {
    interactionMap = defaultInteractionMap;
  }

  const renderLevelIds = isComposite ? [contentSourceId, ...validLevelIds] : validLevelIds;

  const interiorDimensionType = building.settings.planset.interiorDimensionType;
  const levelCategoriesToRender = isComposite
    ? categoriesToRender.filter((cat) => !isCompositeLevelCategory(cat, interiorDimensionType))
    : categoriesToRender;
  const compositeCategoriesToRender = isComposite
    ? categoriesToRender.filter((cat) => isCompositeLevelCategory(cat, interiorDimensionType))
    : [];

  const combinedGeometry: SheetGeometry[] = [];
  const annotationParameterContext = resolveAnnotationParameterContext(building, globalParameterValues);

  for (const levelId of renderLevelIds) {
    const isCompositeLevelId = isCompositeLevelContentSourceId(doc, levelId);
    const filteredCategoriesToRender = isCompositeLevelId ? compositeCategoriesToRender : levelCategoriesToRender;

    // The cutPlaneHeight dictates which slice along the x/z plane we use to take a snapshot of plan geometry.
    // In particular, this affects windows, walls, facades, and wainscoting. Stairs have a separate cut plane.
    // We add a small epsilon to the cutPlaneHeight to prevent degenerate wall cut geometry when the cut plane
    // is tangent to the bottom or top of a window gap. Wainscoting at the exact height of the cutPlane is therefore
    // treated as projected geometry.
    const cutPlaneHeight =
      viewTemplate.cutPlaneHeight !== undefined
        ? levelInchesNew(viewTemplate.cutPlaneHeight + DIST_EPSILON)
        : undefined;

    const wallFinishCutPlaneHeight =
      viewTemplate.wallFinishCutPlaneHeight !== undefined
        ? levelInchesNew(viewTemplate.wallFinishCutPlaneHeight + DIST_EPSILON)
        : undefined;

    const planGeometrySource = new PlanGeometrySource(
      {
        building,
        svgTextScale,
        planRotationRads,
        levelId,
        dimensionPrecision,
        noSettings: true,
        viewTemplateId: docEntityIdFromString("VIEW_TEMPLATE", viewTemplate.id),
        globalParameterValues,
        annotationParameterContext,
        planSetTemplateId,
        planSetTemplateConfigId,
        renderStyleMode,
        cutPlaneHeight,
        stairCutPlaneHeight: viewTemplate.stairCutPlaneHeight,
        wallFinishCutPlaneHeight,
        selectedReportingUnitLabelId: viewport.selectedReportingUnitLabelId,
        generationDiagnostics,
        relaxationInteractionMap: interactionMap,
      },
      planGeometryGenerators,
      initPlanContextIntermediateData,
    );

    // Built-in selectors and filters should not affect composite level wall geometry
    const { builtInSelectors, builtInFilters } =
      isCompositeLevelId || !referenceType
        ? { builtInFilters: {}, builtInSelectors: {} }
        : makeBuiltInPlanTypeSelectorsAndFilters(referenceType, true)(building, levelId, renderStyleMode);

    const filtering = makeSheetGeometrySourceFilter({
      selectorEffectMap,
      builtInSelectors,
      builtInFilters,
      provenanceSelectors,
      reportingUnitSelectors,
    });

    const levelGeometry = await makePlanGeometryList({
      categoriesToRender: filteredCategoriesToRender,
      categoriesToRelax: Object.keys(interactionMap) as readonly SheetGeometryCategory[],
      filtering,
      styling,
      planGeometrySource,
      relaxation: (viewScale, unrelaxedContentAABB) =>
        createGradientDescentRelaxer(
          interactionMap,
          weightMap,
          viewport.contentTextScale ?? 1,
          // If viewScale is not known, calculate approximate fit scale using the unrelaxed geometry source
          viewScale ?? effectiveViewportScale(viewport, unrelaxedContentAABB) ?? DEFAULT_VIEWPORT_SCALE,
          planRotationRads,
          viewport.iterationStep,
          viewport.maxIterationDistance,
        ),
      // This will return an undefined `viewScale` for viewports with `Fit` scaling method.
      // The approximate view scale will be calculated in the relaxation function above.
      viewScale: effectiveViewportScale(viewport),
    });

    combinedGeometry.push(...levelGeometry.geometry);
  }

  return { geometry: combinedGeometry };
}

/**
 * Elevation views stream a fast pass and a slow pass; every other view type answers once.
 * Shared with {@link PlanSetComputeServiceBase.planSetJobNumberOfPromises} so the host's
 * pre-allocated promise count can't drift from what {@link generatePlanGeometryForView} returns.
 */
function renderPlanNumberOfPromises(viewType: RenderPlanRequestPayload["viewType"]): number {
  return viewType === "ELEVATION_VIEW" ? 2 : 1;
}

function generatePlanGeometryForView(
  doc: Doc,
  {
    optionChoices,
    viewType,
    viewTemplate,
    contentSourceId,
    viewport,
    dimensionPrecision,
    renderStyleMode,
    globalParameterValues,
    debugPrefs,
    planSetTemplateId,
    planSetTemplateConfigId,
  }: RenderPlanRequestPayload,
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<RenderGeometryResponsePayload>[] {
  if (!(viewType !== "DRAWING")) {
    console.assert(false);
  }

  if (optionChoices === null) {
    // The worker posts one message per promise while the host waits for exactly
    // renderPlanNumberOfPromises() of them, so this bail-out has to answer the same count --
    // returning one promise for an elevation view leaves the export awaiting a second forever.
    return Array.from({ length: renderPlanNumberOfPromises(viewType) }, () =>
      Promise.resolve({ geometry: { geometry: [] } }),
    );
  }

  switch (viewType) {
    case "PLAN_VIEW": {
      return [
        (async (): Promise<RenderGeometryResponsePayload> => {
          const optionChoiceParamValues = optionChoiceParameterValues(doc, optionChoices);
          const planViewTemplate = viewTemplate as PlanViewTemplate;

          const generatedBuilding = await generateBuildingWithOptionChoices(
            doc,
            optionChoices,
            { ...globalParameterValues, ...optionChoiceParamValues },
            buildingCache,
            enabledFeatures,
            isolatedEntity,
            mirrorOptions,
          );
          const building = generatedBuilding;

          const generationDiagnostics: PlanGeometryGenerationDiagnostic[] = [];
          const geometry = await generatePlanGeometryForBuilding({
            doc,
            building,
            contentSourceId,
            optionChoices,
            viewTemplate: planViewTemplate,
            viewport: viewport as EvaluatedWithOptionSetOverrides<PlanViewport>,
            dimensionPrecision,
            renderStyleMode,
            globalParameterValues,
            debugPrefs,
            planSetTemplateId,
            planSetTemplateConfigId,
            generationDiagnostics,
          });

          return { geometry, generationDiagnostics };
        })(),
      ];
    }
    case "SECTION_VIEW": {
      return [
        new Promise(async (resolve) => {
          const optionChoiceParamValues = optionChoiceParameterValues(doc, optionChoices);
          const generatedBuilding = await generateBuildingWithOptionChoices(
            doc,
            optionChoices,
            { ...globalParameterValues, ...optionChoiceParamValues },
            buildingCache,
            enabledFeatures,
            isolatedEntity,
            mirrorOptions,
          );
          const building = generatedBuilding;

          if (!doc.sectionViews[contentSourceId] || viewType !== viewTemplate.viewType) {
            resolve({ geometry: { geometry: [] } });
          }

          const planGeometrySource = new PlanGeometrySource(
            {
              building,
              sectionViewId: contentSourceId,
              globalParameterValues,
              includeGradeLine: !!enabledFeatures?.modeledGradeLine,
              // A viewport takes its source from the template, not from the section view.
              gradeLineSource:
                enabledFeatures?.modeledGradeLine && isSectionViewTemplate(viewTemplate)
                  ? viewTemplate.gradeLineSource ?? GradeLineSource.DEFAULT
                  : GradeLineSource.DEFAULT,
            },
            sectionGeometryGenerators,
            initSectionContextIntermediateData,
          );

          const { categories } = viewTemplate;

          // `defaultStyles` omitted: it falls back to `planDefaultCategoryStyles`, which sections draw with.
          // Grade line only -- honoring the template's other overrides here restyles existing sections (SDO-6280).
          const gradeLineOverride = viewTemplate.styleOverrides[SheetGeometryCategory.GRADE_LINE];
          const styling = makeSheetGeometryStyler({
            viewSpecificStyles: gradeLineOverride ? { [SheetGeometryCategory.GRADE_LINE]: gradeLineOverride } : {},
          });

          resolve({
            geometry: await makePlanGeometryList({
              categoriesToRender: categories,
              planGeometrySource,
              styling,
            }),
          });
        }),
      ];
    }
    case "ELEVATION_VIEW": {
      let slowResolve, slowReject;
      const slowPromise = new Promise<RenderGeometryResponsePayload>((res, rej) => {
        slowResolve = res;
        slowReject = rej;
      });
      return [
        new Promise(async (resolve, reject) => {
          let slowError: Error | undefined = undefined;
          const generationDiagnostics: PlanGeometryGenerationDiagnostic[] = [];
          const optionChoiceParamValues = optionChoiceParameterValues(doc, optionChoices);
          const generatedBuilding = await generateBuildingWithOptionChoices(
            doc,
            optionChoices,
            { ...globalParameterValues, ...optionChoiceParamValues },
            buildingCache,
            enabledFeatures,
            isolatedEntity,
            mirrorOptions,
          );
          const building = generatedBuilding;
          const eyePosAndUpVec = viewportEyePosAndUpVec(
            viewport.viewType,
            viewport.contentSourceId,
            doc,
            building.mirror,
            building.mirrorTranslationX,
          );
          if (!eyePosAndUpVec) {
            resolve({ geometry: { geometry: [] } });
            slowResolve({ geometry: { geometry: [] } });
            return;
          }
          const { eyePos } = eyePosAndUpVec;
          const elevationView = doc.elevationViews[contentSourceId];

          if (
            !elevationView ||
            !isElevationViewTemplate(viewTemplate) ||
            !isElevationViewport(viewport) ||
            !viewTemplateContainsElevationAnnotation(viewTemplate)
          ) {
            resolve({ geometry: { geometry: [] } });
            slowResolve({ geometry: { geometry: [] } });
            return;
          }

          const referenceFrame = elevationView.referenceFrame ?? "FRONT";
          const elevationStartTime = performance.now();

          // Convert the level annotation params to sheet scale
          let elevationAnnotationParams: ElevationAnnotationParams | undefined = undefined;
          if (viewport.scalingMethod === ViewportScalingMethod.EXPLICIT && viewport.elevationAnnotationParams) {
            const viewportScale = effectiveViewportScale(viewport);
            assertIsNotUndefined(viewportScale);
            elevationAnnotationParams = await scaleElevationAnnotationParams(
              building,
              eyePos,
              viewport.elevationAnnotationParams,
              viewportScale,
            );
          }

          const selectorEffectMap = makeSortedSelectorEffectMap(
            viewport.viewTemplateOverrideIds,
            viewTemplate.viewTemplateOverrideIds,
            doc,
          );
          const combinedSelectorEffectMap = selectorEffectMap;

          const styling = makeSheetGeometryStyler({
            selectorEffectMap: combinedSelectorEffectMap,
            viewSpecificStyles: viewTemplate.styleOverrides,
            effects: doc.effects,
            defaultStyles: elevationDefaultCategoryStyles,
          });

          // Wrap the styler to apply viewport scale to font sizes for proper bounding box calculation
          // This is similar to how scaleElevationAnnotationParams scales annotation params
          const viewportScale = effectiveViewportScale(viewport);
          const elevationScale = await getElevationScaleFactor({ building, eyePos });
          const relativeScale = viewportScale ? elevationScale / viewportScale : 1;
          const elevationStyling: SheetGeometryStyler = (geometry, category) => {
            const styled = styling(geometry, category);
            // Scale font sizes by the relative scale for accurate bounding box calculation
            return styled.map((g) => {
              if (g.type === SheetGeometryType.TEXT) {
                if (g.fontSize !== undefined) {
                  g = { ...g, fontSize: numMul(g.fontSize, relativeScale) };
                }
                if (g.boxWidth !== undefined) {
                  g = { ...g, boxWidth: numMul(g.boxWidth, relativeScale) };
                }
              }
              return g;
            });
          };

          const filtering = makeSheetGeometrySourceFilter({
            selectorEffectMap: combinedSelectorEffectMap,
          });

          const elevationDebugOptions: ElevationDebugOptions = {
            debugElevationColliders: !!debugPrefs?.debugElevationColliders,
            showElevationNoteEntities:
              debugPrefs?.showElevationNoteEntities ?? defaultElevationDebugOptions.showElevationNoteEntities,
          };

          const context: ElevationGenerationContext = {
            building,
            eyePos,
            referenceFrame,
            textScalingFactor: viewport.contentTextScale ?? 1,
            dimensionPrecision: DimensionPrecision.PRECISION_1_8, // TODO: take it from the entity or the view
            // Always generated, even when the category is hidden. `makePlanGeometryList` filters
            // GRADE_LINE out of the geometry list below, but it cannot un-reserve the paper space:
            // `addPaperSpaceGeometry` feeds every geometry to the collision system before the
            // builder, and `generateSlowElevationAnnotations` re-runs the fast pass purely to
            // populate that collision system. Skipping generation would free the band at grade and
            // let MATERIAL_TAGS move into it, changing sheets that hide the grade line today.
            includeGradeLine: true,
            // The template's source, not the view's: a viewport takes its settings from the template
            // and only its content from the view. See `ElevationViewTemplate.gradeLineSource`.
            //
            // Forced to DEFAULT when the category is hidden (the flat line is still needed as a
            // collider, but walking the site's terrain for a line nobody sees is not) and while the
            // flag is off; see the field's doc for why the flag gates rendering.
            gradeLineSource:
              viewTemplate.categories.includes(SheetGeometryCategory.GRADE_LINE) && enabledFeatures?.modeledGradeLine
                ? viewTemplate.gradeLineSource ?? GradeLineSource.DEFAULT
                : GradeLineSource.DEFAULT,
            allowBelowGroundAnnotations:
              (viewTemplate.belowGradeRenderStyle ?? PlanLineStyle.NONE) !== PlanLineStyle.NONE,
            elevationDebugOptions,
            elevationAnnotationParams,
            sheetGeometryStyler: elevationStyling,
            categoriesToExcludeFromCollisions: [
              SheetGeometryCategory.ELEVATION_MARKERS,
              SheetGeometryCategory.TOP_OF_PLATE_MARKERS,
              SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS,
            ].filter((category) => !viewTemplate.categories.includes(category)),
            viewportScale,
            viewportOrViewName: viewport.name || elevationView.name,
            visibilitySelectors: combinedSelectorEffectMap,
            noSettings: true,
            globalParameterValues,
            selectedReportingUnitLabelId: viewport.selectedReportingUnitLabelId,
            occlusionEngineOverride: elevationOcclusionEngineForFeatures(building.enabledFeatures),
          };

          const planGeometrySource = new PlanGeometrySource(
            context,
            elevationGeometryGenerators,
            initElevationContextIntermediateData,
            slowElevationGeometryGenerators,
            async (_source, _category, { diagnostics, error }) => {
              if (slowError) {
                return;
              }
              generationDiagnostics.push(...(diagnostics ?? []));
              if (error) {
                slowError = error;
                captureException(error);
                console.error(`Failed to render elevation annotation overlay: ${error}`);
                return;
              }
            },
            async () => {
              if (slowError) {
                slowReject(slowError);
                return;
              }
              try {
                const geometry = await makePlanGeometryList({
                  // The debug category is added here in case there's content created for it. Nothing is created under normal circumstances.
                  categoriesToRender: [
                    SheetGeometryCategory.DEBUG_ELEVATION_COLLIDERS,
                    SheetGeometryCategory.DEBUG_ELEVATION_NOTE_ENTITIES,
                    ...viewTemplate.categories,
                  ],
                  filtering,
                  styling,
                  planGeometrySource,
                });
                const timingMs = planGeometrySource.slowGeneratorTimingMs;
                slowResolve({
                  geometry,
                  generationDiagnostics,
                  timingMs: { ...timingMs, slowGeometry: performance.now() - elevationStartTime },
                });
              } catch (error) {
                slowError = error instanceof Error ? error : new Error(error.toString());
                slowReject(slowError);
              }
            },
          );
          try {
            const geometry = await makePlanGeometryList({
              categoriesToRender: viewTemplate.categories,
              filtering,
              styling,
              planGeometrySource,
              debugPrefs,
            });
            const fastGeometryMs = performance.now() - elevationStartTime;
            resolve({ geometry, timingMs: { fastGeometry: fastGeometryMs } });
            if (!planGeometrySource.containsSlowGenerators(viewTemplate.categories)) {
              // Timing data omitted, since no slow generators were run.
              slowResolve({ geometry });
            }
          } catch (error) {
            slowError = error instanceof Error ? error : new Error(error.toString());
            reject(new Error(`Failed to render elevation geometry: ${error}`));
          }
        }),
        slowPromise,
      ];
    }
    default:
      return [Promise.resolve({ geometry: { geometry: [] } })];
  }
}

function getDataTableRowsSlice<T>(rows: T[], firstRow: number | undefined, maxRowCount: number | undefined): T[] {
  const firstRowIndex = (firstRow ?? 1) - 1;
  const lastRowIndex = firstRowIndex + (maxRowCount ?? rows.length);
  return rows.slice(firstRowIndex, lastRowIndex);
}

export function customDataSequenceFromSheetTitles(pages: SheetTitle[] = []): CustomDataSequence {
  return {
    id: "",
    name: "",
    sortIndex: 0,
    entityType: "DATA_SEQUENCE",
    sequenceType: "CUSTOM",
    cols: CUSTOM_SHEET_DATA_SEQUENCE_COLUMNS,
    rows: pages.map((x) => ({ name: x.name, number: x.number })),
  };
}

/**
 * Sorts table rows based on a specified column and sort order.
 * Works with string & numerical column titles
 */
function sortTableRows<T extends Record<string, unknown>>(
  rows: T[],
  sortColumnId: string | undefined,
  sortOrder: DataTableColumnSortOrder | undefined,
): T[] {
  if (!sortColumnId || !sortOrder) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    const aValue = (a[sortColumnId]?.toString() ?? "").trim();
    const bValue = (b[sortColumnId]?.toString() ?? "").trim();

    const tokensA = aValue.split(/\s+/);
    const tokensB = bValue.split(/\s+/);

    // Handle numeric values
    const aNum = Number(tokensA[0]);
    const bNum = Number(tokensB[0]);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      return sortOrder === "ASCENDING" ? aNum - bNum : bNum - aNum;
    }

    // Handle string values
    const compareResult = aValue.localeCompare(bValue);
    return sortOrder === "ASCENDING" ? compareResult : -compareResult;
  });
}

// TODO: break this into two job types - one for custom data sequences and one for built-in data sequences
// only built-in data sequences need to generate the building
export async function generateDataTableContents(
  doc: Doc,
  dataTable: EvaluatedDataTable,
  pages?: SheetTitle[],
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  pricingContext?: DataTablePricingContext,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<GetTableContentsResponsePayload> {
  const seq =
    dataTable.dataSequenceId === SHEETS_DATA_SEQUENCE_ID
      ? customDataSequenceFromSheetTitles(pages)
      : doc.dataSequences[dataTable.dataSequenceId];

  const totalTableWidth = dataTable.width ?? sheetInchesNew(100);
  let fontSize = STANDARD_FONT_SIZE;
  if (dataTable.fontSize) {
    fontSize = dataTable.fontSize;
  }

  const builtInDataSequenceType = dataTable.dataSequenceId as SystemDataSequenceEntityType;

  const dataTableHeaderLabelOverrides =
    BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_TO_ATTRIBUTE_DISPLAY_NAME[builtInDataSequenceType];

  const PADDING_SIZE = sheetPointsNew(5);
  const columnConfig = dataTable.columnConfig ?? {};
  const findAndReplace = dataTable.findAndReplace ?? {};

  const sortByColumnId = (() => {
    if (dataTable.sortColumnId && dataTableHeaderLabelOverrides?.[dataTable.sortColumnId]) {
      return dataTableHeaderLabelOverrides[dataTable.sortColumnId];
    }

    return dataTable.sortColumnId;
  })();

  function applyFindAndReplace(value: number | string, columnId: string, isAggregated: boolean = false) {
    const id = encodeColumnConfigId(columnId, { isAggregated });

    const r = Object.values(findAndReplace).filter((x) => x.columnId === id);

    if (r.length) {
      value = value?.toString() ?? "";

      for (const { find, replace } of r) {
        value = replaceAll(value, find, replace);
      }
    }

    return value;
  }

  const dataCellStyle: TableCellStyle = {
    verticalAlignment: TextVerticalAlignment.TOP,
    padding: PADDING_SIZE,
    alignment: TextAlignment.CENTER,
    border: false,
    borderEdges: {
      bottom: true,
      top: false,
      left: false,
      right: true,
    },
  };

  const headerCellStyle: TableCellStyle = {
    verticalAlignment: TextVerticalAlignment.CENTER,
    padding: PADDING_SIZE,
    border: false,
    borderEdges: {
      bottom: true,
      top: false,
      left: false,
      right: true,
    },
  };

  const titleConfig = resolveDataTableTitleConfig(dataTable, doc);

  if (seq?.sequenceType === "CUSTOM") {
    const rows: TableCellProps[][] = [];

    // filter hidden columns
    const cols = seq.cols
      .filter((x) => !columnConfig[x.id]?.isHidden)
      .map((x) => ({ ...x, name: columnConfig[x.id]?.displayName ?? x.name }))
      .sort((a, b) => (columnConfig[a.id]?.index ?? 0) - (columnConfig[b.id]?.index ?? 0));

    const columnWidths: { [key: string]: SheetInches | undefined } = {};
    const columnOrder = cols.map((col) => col.id);
    for (const col of cols) {
      const width = columnConfig[col.id]?.width;
      if (width) {
        columnWidths[col.id] = sheetInchesNew(width);
      } else {
        columnWidths[col.id] = undefined;
      }
    }

    const tableWidths: DataTableColumnWidths = {
      tableWidth: totalTableWidth,
      columnWidths,
      columnOrder,
    };

    rows.push(
      cols.map((col) => {
        return {
          cellStyle: headerCellStyle,
          contentType: TableCellContentType.TEXT,
          contents: {
            type: SheetGeometryType.TEXT,
            fontSize,
            x: 0,
            y: 0,
            text: col.name,
            alignment: TextAlignment.CENTER,
          },
        };
      }),
    );

    let sortedRows = sortTableRows(seq.rows, dataTable.sortColumnId, dataTable.sortColumnIdOrder);

    // restrict rows to firstRow and maxRowCount
    sortedRows = getDataTableRowsSlice(sortedRows, dataTable.firstRow, dataTable.maxRowCount);

    rowForLoop: for (const scheduleRow of sortedRows) {
      const colValues = cols.map((col) => scheduleRow[col.id]?.toString() ?? "");

      // filter hidden rows
      for (let i = 0; i < colValues.length; i++) {
        const col = colValues[i];
        const id = cols[i].id;

        if (!attributeFilterCheck({ ...columnConfig[id], definesEntity: true }, col)) {
          continue rowForLoop;
        }
      }

      const rr: TableCellProps[] = [];
      for (const col of cols) {
        const cellContent = scheduleRow[col.id]?.toString() ?? "";
        const cell: TableCellProps =
          col.type === "SYMBOL"
            ? {
                cellStyle: dataCellStyle,
                contentType: TableCellContentType.SVG,
                contents: {
                  type: SheetGeometryType.SVG,
                  path: cellContent,
                },
              }
            : {
                cellStyle: dataCellStyle,
                contentType: TableCellContentType.TEXT,
                contents: {
                  type: SheetGeometryType.TEXT,
                  fontSize,
                  x: inches(0),
                  y: inches(0),
                  text: applyFindAndReplace(cellContent, col.id),
                  alignment: TextAlignment.CENTER,
                },
              };

        rr.push(cell);
      }

      rows.push(rr);
    }

    // Create the table node with border
    const tableNode = makeTable({
      width: totalTableWidth,
      rows,
      x: inches(0),
      y: inches(0),
      columnWidths: tableWidths,
    });

    // If title is hidden, just return the table
    if (dataTable.titleHidden) {
      return {
        contents: tableNode,
      };
    }

    return {
      contents: {
        ...tableNode,
        style: makeSheetNodeStyleSheet({
          [SheetNodeType.TABLE]: {
            border: true,
            strokeColor: colors.stoneDarkX1,
            strokeOpacity: percentNew(1),
          },
        }),
        title: {
          text: titleConfig.title,
          fontSize: titleConfig.titleFontSize,
          alignment: titleConfig.titleAlignment,
        },
      },
    };
  }

  const catalogSequenceSelector = BUILTIN_DATASEQUENCE_CATALOG[builtInDataSequenceType];

  if (!catalogSequenceSelector) {
    return {
      contents: makeTable({
        x: inches(0),
        y: inches(0),
        width: totalTableWidth,
        height: undefined,
        rows: [],
        columnWidths: { tableWidth: ZERO_INCHES, columnWidths: {}, columnOrder: [] } as DataTableColumnWidths,
      }),
    };
  }

  const defaultSettings = getDataTableScheduleSettingsWithSymbols(
    builtInDataSequenceType,
    dataTable.name,
    columnConfig,
  );

  for (const colId in columnConfig) {
    const config = columnConfig[colId];
    const { id, isAggregated } = decodeColumnConfigId(colId);

    if (isAggregated) {
      if (defaultSettings.measures[id]) {
        defaultSettings.measures[id] = {
          ...defaultSettings.measures[id],
          ...config,
        };
      }
    } else {
      if (defaultSettings.attributes[id]) {
        defaultSettings.attributes[id] = {
          ...defaultSettings.attributes[id],
          ...config,
        };
      }
    }
  }

  const optionChoices = resolveOptionChoices(doc, dataTable);

  if (optionChoices === null) {
    return {
      contents: makeTable({
        x: inches(0),
        y: inches(0),
        width: dataTable.width,
        height: undefined,
        rows: [],
        columnWidths: { tableWidth: ZERO_INCHES, columnWidths: {}, columnOrder: [] },
      }),
    };
  }

  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    {},
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );

  const res = makeBuiltInDataTables(
    building,
    builtInDataSequenceType,
    [defaultSettings],
    undefined,
    applyFindAndReplace,
    makeDataTableKAMRowCellGenerator(builtInDataSequenceType, building, columnConfig),
    makeDataTableKAMHeaderCellGenerator(builtInDataSequenceType, columnConfig),
    makeDataSequenceKAMFilter(builtInDataSequenceType, dataTable, building),
    true,
    builtInDataSequenceType === "BUILDING_OPTION" ? pricingContext : undefined,
  );

  if (res.length === 0) {
    return {
      contents: makeTable({
        x: inches(0),
        y: inches(0),
        width: dataTable.width,
        height: undefined,
        rows: [],
        columnWidths: { tableWidth: ZERO_INCHES, columnWidths: {}, columnOrder: [] },
      }),
    };
  }

  let table = res[0];
  let tableWidths: DataTableColumnWidths;

  // KAM sequence legacy configuration
  // TODO - unify this and the below code, remove KAM_SEQUENCE, and get rid
  // of cell generators inside the makeBuiltInDataTables (used for KAM_SEQUENCE types)
  if (catalogSequenceSelector.dataSequenceType === "KAM_SEQUENCE") {
    if (sortByColumnId) {
      function isSortByColumn(columnId: string) {
        if (!sortByColumnId) {
          return false;
        }

        return sortByColumnId.toLowerCase() === columnId.toLowerCase();
      }

      const columnIndex = table.headers.findIndex(isSortByColumn);

      if (columnIndex !== -1) {
        // Normalize the schedule rows to a format compatible with sortTableRows
        const rowsForSorting = table.rows.map((row) => ({
          [dataTable.sortColumnId as string]: row[columnIndex]?.value,
        }));

        // Use the generic sortTableRows function
        const sortedRows = sortTableRows(rowsForSorting, dataTable.sortColumnId, dataTable.sortColumnIdOrder);

        // Map the sorted indices back to the original schedule rows
        table.rows = sortedRows.map((_, index) => table.rows[rowsForSorting.indexOf(sortedRows[index])]);
      }
    }

    // Resolve column widths
    const headerIds = getScheduleHeaderIdsOrdered(defaultSettings, builtInDataSequenceType, columnConfig);
    const columnWidths: { [key: string]: SheetInches | undefined } = {};
    for (const headerId of headerIds) {
      const width = columnConfig[headerId]?.width;
      if (width) {
        columnWidths[headerId] = sheetInchesNew(width);
      } else {
        columnWidths[headerId] = undefined;
      }
    }

    tableWidths = {
      tableWidth: totalTableWidth,
      columnWidths,
      columnOrder: headerIds,
    };
  } else {
    const tableConfig = configureBuiltInDataTable(dataTable, table, columnConfig, sortByColumnId);
    table = tableConfig.table;
    tableWidths = {
      tableWidth: totalTableWidth,
      columnWidths: tableConfig.tableConfig.columnWidths,
      columnOrder: table.headers,
    };
  }

  table.rows = getDataTableRowsSlice(table.rows, dataTable.firstRow, dataTable.maxRowCount);

  const tableRows: TableCellProps[][] = [];

  // Add column headers
  if (!dataTable.columnHeadersHidden) {
    tableRows.push(
      table.headers.map((header) => {
        return {
          cellStyle: headerCellStyle,
          contentType: TableCellContentType.TEXT,
          contents: {
            type: SheetGeometryType.TEXT,
            fontSize,
            x: 0,
            y: 0,
            text: header,
            alignment: TextAlignment.CENTER,
          },
        };
      }),
    );
  }

  for (const scheduleRow of table.rows) {
    tableRows.push(
      scheduleRow.map((cell) => {
        if (cell.type === "SYMBOL") {
          return {
            cellStyle: dataCellStyle,
            contentType: TableCellContentType.SVG,
            contents: {
              type: SheetGeometryType.SVG,
              path: cell.value ?? emptySVG(),
            },
          };
        } else {
          return {
            cellStyle: dataCellStyle,
            contentType: TableCellContentType.TEXT,
            contents: {
              type: SheetGeometryType.TEXT,
              fontSize,
              x: inches(0),
              y: inches(0),
              text: cell.value?.toString(),
              alignment: TextAlignment.CENTER,
            },
          };
        }
      }),
    );
  }

  const tableNode = makeTable({
    width: totalTableWidth,
    rows: tableRows,
    x: inches(0),
    y: inches(0),
    columnWidths: tableWidths,
  });
  // If title is hidden, just return the table
  if (dataTable.titleHidden) {
    return {
      contents: tableNode,
    };
  }

  return {
    contents: {
      ...tableNode,
      style: makeSheetNodeStyleSheet({
        [SheetNodeType.TABLE]: {
          border: true,
          strokeColor: colors.stoneDarkX1,
          strokeOpacity: percentNew(1),
        },
      }),
      title: {
        text: titleConfig.title,
        fontSize: titleConfig.titleFontSize,
        alignment: titleConfig.titleAlignment,
      },
    },
  };
}

async function collectBuilding3DGeometry(building: Building) {
  console.time("collectBuilding3DGeometry");
  // Note that this does not filter the faces.
  // In case of preview, the faces are filtered in the previewer, not in the service to improve responsiveness of adjusting the filter.
  const groups = collectBuildingFacesByCategory({
    building,
    buildMask: faceCategoryFilterFromSheetGeometryCategories(sheetGeometry3DCategories),
  });

  const propsCategories = new Set<FaceCategory>([
    FaceCategory.PROP_APPLIANCES,
    FaceCategory.PROP_FIXTURES,
    FaceCategory.PROP_FURNITURE,
    FaceCategory.PROP_OTHER,
    FaceCategory.WINDOW_DECORATION,
    FaceCategory.PROP_FOLIAGE,
    ...doorFaceCategories,
  ]);
  const props = collectBuildingProps(building, propsCategories);
  const groundZ = buildingGroundZ(building);
  console.timeEnd("collectBuilding3DGeometry");
  return { groups, props, groundZ };
}

async function collectBuilding3DGeometryWithCache(
  doc: Doc,
  optionChoices: OptionChoices,
  buildingCache?: BuildingCache,
  building3DGeometryCache?: Building3DGeometryCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<{
  groups: ReturnType<typeof collectBuildingFacesByCategory>;
  props: ReturnType<typeof collectBuildingProps>;
  groundZ: number;
}> {
  const globalParamValues = {}; // Never needed for 3D geometry output.
  const buildingCacheKey: BuildingCacheKey = {
    doc,
    optionChoices,
    globalParamValues,
    isolatedEntity,
    mirrorInvert: mirrorOptions?.mirrorInvert,
    mirrorTranslationXOverride: mirrorOptions?.mirrorTranslationXOverride,
  };

  if (building3DGeometryCache) {
    const cached = getBuilding3DGeometryCachedContext(buildingCacheKey, building3DGeometryCache);
    if (cached) {
      return cached;
    }
  }

  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const result = await collectBuilding3DGeometry(building);

  if (building3DGeometryCache) {
    building3DGeometryCache.add(buildingCacheKey, result);
  }

  return result;
}

async function collectPerspectiveViewGeometry(
  doc: Doc,
  { contentSourceId, optionChoices }: GetPerspectiveViewGeometryRequestPayload & { optionChoices: OptionChoices },
  buildingCache?: BuildingCache,
  building3DGeometryCache?: Building3DGeometryCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<GetPerspectiveViewGeometryResponsePayload> {
  const globalParamValues = {}; // Never needed in this view.
  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const { groups, props, groundZ } = await collectBuilding3DGeometryWithCache(
    doc,
    optionChoices,
    buildingCache,
    building3DGeometryCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const camera = viewTemplateCamera3D(building, contentSourceId as ViewportCameraKey);
  const ndcBB = await renderNDCBBFromBuilding({ building, camera });
  return {
    groups,
    props,
    ndcBB,
    camera,
    groundZ,
    propCatalog: building.settings.props,
    exteriorPalette: building.exteriorPalette,
    interiorPalette: building.interiorPalette,
    mirror: building.mirror,
    mirrorTranslationX: building.mirrorTranslationX,
  };
}

/**
 * View-space bounding box of what the selected reporting unit renders, foundation included.
 * Undefined when the unit renders nothing.
 */
function reportingUnitViewSpaceBB(
  doc: Doc,
  groups: Table<FaceGroupBSP[]>,
  viewMatrix: Mat4,
  selectedReportingUnitLabelId: string,
  viewTemplateCategories: readonly SheetGeometryCategory[],
) {
  const reportingUnitLabelIds = getReportableUnitDesignatorLabelIds(doc);
  const memo = new Map<string, string | undefined>();
  const faces: Face[] = [];
  for (const faceCategoryString in groups) {
    const faceCategory = parseInt(faceCategoryString) as FaceCategory;
    if (!viewTemplateLineworkHasFaceCategory(viewTemplateCategories, faceCategory)) {
      continue;
    }
    for (const faceGroup of groups[faceCategoryString]) {
      if (
        resolveItemReportingUnitLabelId(doc, reportingUnitLabelIds, faceGroup, memo) === selectedReportingUnitLabelId
      ) {
        faces.push(...faceGroup.faces);
      }
    }
  }
  return faces.length > 0 ? renderViewSpaceBBFromVertices({ viewMatrix, faces }) : undefined;
}

async function collectOrthographicViewGeometry(
  doc: Doc,
  {
    viewType,
    optionChoices,
    contentSourceId,
    selectedReportingUnitLabelId,
    viewTemplate,
  }: GetOrthographicViewGeometryRequestPayload & { optionChoices: OptionChoices },
  buildingCache?: BuildingCache,
  building3DGeometryCache?: Building3DGeometryCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<GetOrthographicViewGeometryResponsePayload> {
  // We don't use the eye position from viewportEyePosAndUpVec because we want the "worldBB" coordinates to be relative to the world origin,
  // even though the rotation of worldBB depends on the view direction.
  const globalParamValues = {}; // Never needed for orthographic geometry output.
  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const { viewDirection, upVector } = ensureDefined(
    viewportEyePosAndUpVec(viewType, contentSourceId, doc, building.mirror, building.mirrorTranslationX),
  );
  const viewMatrix = mat4LookAt([0, 0, 0], viewDirection, upVector);

  const building3DGeometry = await collectBuilding3DGeometryWithCache(
    doc,
    optionChoices,
    buildingCache,
    building3DGeometryCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );
  const unitWorldBB = selectedReportingUnitLabelId
    ? reportingUnitViewSpaceBB(
        doc,
        building3DGeometry.groups,
        viewMatrix,
        selectedReportingUnitLabelId,
        viewTemplate.categories,
      )
    : undefined;
  const worldBB =
    unitWorldBB ??
    (await orthographicDrawingBB(building, viewMatrix, false, undefined, doc.sectionViews[contentSourceId]));

  return {
    ...building3DGeometry,
    worldBB,
    propCatalog: building.settings.props,
    exteriorPalette: building.exteriorPalette,
    interiorPalette: building.interiorPalette,
    mirror: building.mirror,
    mirrorTranslationX: building.mirrorTranslationX,
  };
}

async function generateSectionIntersections(
  doc: Doc,
  { optionChoices, contentSourceId, selectedReportingUnitLabelId }: RenderSectionIntersectionsRequestPayload,
  buildingCache?: BuildingCache,
  enabledFeatures?: GenerateBuildingFeatureSet,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorOptions?: GenerateBuildingMirrorOptions,
): Promise<RenderSectionIntersectionsResponsePayload> {
  const sectionView = doc.sectionViews[contentSourceId];

  // The section cut poché spans the whole building and isn't attributable per reporting unit, so it
  // is hidden while filtered to a unit; the filtered 3D linework still shows the unit's cut view.
  if (!sectionView || optionChoices === null || selectedReportingUnitLabelId) {
    return { sectionIntersections: {} };
  }

  const globalParamValues = {}; // Never needed for section intersections output.
  const building = await generateBuildingWithOptionChoices(
    doc,
    optionChoices,
    globalParamValues,
    buildingCache,
    enabledFeatures,
    isolatedEntity,
    mirrorOptions,
  );

  // Note that section intersections don't take view template override visibility effects into account.
  // Currently the only 3D geometry the overrides can affect is props, and those are not rendered in the section intersections.
  const sectionIntersections = renderSectionFrontPlaneIntersectionsGeometry(building, sectionView);
  return { sectionIntersections };
}

interface ScheduleTypeSettingsWithSymbol extends BuiltInDataTableSettings {
  readonly symbols: Table<DataTableColumnConfig>;
}

export function getDataTableScheduleSettingsWithSymbols(
  seqId: SystemDataSequenceEntityType,
  tableName: string,
  columnConfig: Table<DataTableColumnConfig>,
): ScheduleTypeSettingsWithSymbol {
  if (BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_INCLUDES_SYMBOL_COLUMN[seqId]) {
    return {
      ...getDefaultBuiltInDataTableSettings(seqId, tableName),
      symbols: {
        [DATA_TABLE_SYMBOL_COLUMN_ID]: {
          ...columnConfig?.[DATA_TABLE_SYMBOL_COLUMN_ID],
        },
      },
    };
  }

  return { ...getBuiltInDataTableSettings(seqId, tableName), symbols: {} };
}

function getScheduleHeaderIdsOrdered(
  settings: ScheduleTypeSettingsWithSymbol,
  builtInDataSequenceType: SystemDataSequenceEntityType,
  columnConfig: Table<DataTableColumnConfig>,
): string[] {
  const headerGenerator = makeDataTableKAMHeaderCellGenerator(builtInDataSequenceType, columnConfig);
  const headers = extractBuiltInDataTableSettingHeaders(settings, headerGenerator);
  return Object.entries(headers)
    .sort(([, a], [, b]) => (a.index ?? Infinity) - (b.index ?? Infinity))
    .map(([key]) => key);
}

function configureBuiltInDataTable(
  dataTableSource: EvaluatedDataTable,
  dataTable: BuiltInDataTable,
  dataTableColumnConfig: Table<DataTableColumnConfig>,
  sortByColumnId?: string,
) {
  function isSortByColumn(columnId: string) {
    if (!sortByColumnId) {
      return false;
    }

    return sortByColumnId.toLowerCase() === columnId.toLowerCase();
  }

  const tableConfig: {
    columnWidths: Record<string, SheetInches | undefined>;
  } = { columnWidths: {} };

  let needColumnSort = false;

  const originalHeaderCount = dataTable.headers.length;

  const headerConfig = dataTable.headers
    .map((header, headerIndex) => {
      const config = dataTableColumnConfig[header];
      const columnName = config?.displayName ?? header;
      const columnSortIndex = config?.index ?? 0;
      const columnWidth = config?.width ? sheetInchesNew(config.width) : undefined;

      const isHidden = config?.isHidden ?? false;

      return { header, headerIndex, columnName, columnSortIndex, columnWidth, isHidden };
    })
    .filter((x) => !x.isHidden)
    .sort((a, b) => a.columnSortIndex - b.columnSortIndex)
    .map((x, columnIndex) => {
      if (columnIndex !== x.headerIndex) {
        needColumnSort = true;
      }

      // only add column widths to the tableConfig if not hidden
      tableConfig.columnWidths[x.columnName] = x.columnWidth;

      return {
        ...x,
        columnIndex,
      };
    });

  // sort headers by columnConfig.index
  dataTable.headers = headerConfig.map((x) => x.columnName);

  // sort columns by columnConfig.index
  if (needColumnSort || originalHeaderCount !== headerConfig.length) {
    for (let rowIndex = 0; rowIndex < dataTable.rows.length; rowIndex++) {
      const row = dataTable.rows[rowIndex];
      const newRow = new Array(dataTable.headers.length);

      for (let i = 0; i < headerConfig.length; i++) {
        const fromIndex = headerConfig[i].headerIndex;
        const toIndex = headerConfig[i].columnIndex;
        newRow[toIndex] = row[fromIndex];
      }

      dataTable.rows[rowIndex] = newRow;
    }
  }

  // sort rows by sortColumnId
  if (sortByColumnId) {
    const columnIndex = dataTable.headers.findIndex(isSortByColumn);

    if (columnIndex !== -1) {
      // Normalize the schedule rows to a format compatible with sortTableRows
      const rowsForSorting = dataTable.rows.map((row) => ({
        [dataTableSource.sortColumnId as string]: row[columnIndex]?.value,
      }));

      // Use the generic sortTableRows function
      const sortedRows = sortTableRows(rowsForSorting, dataTableSource.sortColumnId, dataTableSource.sortColumnIdOrder);

      // Map the sorted indices back to the original schedule rows
      dataTable.rows = sortedRows.map((_, index) => dataTable.rows[rowsForSorting.indexOf(sortedRows[index])]);
    }
  }

  return { tableConfig, table: dataTable };
}

function isCompositeLevelCategory(cat: SheetGeometryCategory, interiorDimensionType: InteriorDimensionType) {
  return (
    COMPOSITE_LEVEL_CATEGORIES.has(cat) ||
    // Interior dimensions can only be calculated for the BY_ROOM_GROUP type because this
    // does not require constructing a full planar graph for the composite level.
    (interiorDimensionType === InteriorDimensionType.BY_ROOM_GROUP && COMPOSITE_INTERIOR_DIM_CATEGORIES.has(cat))
  );
}
